import { useQuery } from 'react-query'
import { Layout, Text } from '@staccx/bento'
import { RightAlignedRow } from '../../../../theme/Theme.ItemGroup'
import { TranslatedText } from '@staccx/i18n'
import Table from '../../../../components/tables/Table'
import arraySplit from '../../../../utils/arraySplit'
import { formatCurrency, sum } from '../../../../utils/tableFuncs'
import { fetchInvoiceDetails } from '../../../../api/api'
import { useParams } from 'react-router-dom'
import InvoiceReportDetailsTable from '../components/InvoiceReportDetailsTable'
import { fetchGroupRows, mapNestedRows } from '../components/InvoiceReportDetailsTable/helper'
import { groupBy, sumBy } from 'lodash'
import { Heading } from '../../../../components/text/Headings'
import { ExcelExportButton, TableExcelExportButton } from '../components/ExcelExporter'
import { Loader } from '../../../cars/components/V3/_shared/Loader'

const InvoiceReportDetails = () => {
  const { invoiceNo } = useParams()
  const { data: queryData = [], isLoading } = useQuery(
    ['invoiceReportDetails', invoiceNo],
    () => fetchInvoiceDetails(invoiceNo),
    {
      enabled: !!invoiceNo,
    },
  )

  if (isLoading) return <Loader />

  const data = Array.isArray(queryData) ? queryData : (queryData?.data ?? [])
  const linesWithInvoiceInfo = data
    .map((invoice) => {
      const { invoiceDetails } = invoice
      const mappedInvoiceDetails = invoiceDetails.map((detail) => ({ ...invoice, ...detail }))

      return { ...invoice, invoiceDetails: mappedInvoiceDetails }
    })
    .flatMap((invoice) => invoice.invoiceDetails || [])

  // Don't touch this as it organizes the tables based on invoiceGroupDescription
  const splitLines = arraySplit(linesWithInvoiceInfo, 'invoiceGroupDescription', 'NO_INVOICE_GROUP')
  const sumTotal = sum(linesWithInvoiceInfo, (row) => row.amountIncVAT)

  // NEW TABLE DATA FORMATTING METHOD
  const collapsableData = (invoices) => {
    return Object.entries(groupBy(invoices, 'accountNo')).map(([key, value]) => {
      const accountNoValues = value.map((e) => {
        return { ...e, sourceID: e?.docuBizzSourceID }
      })

      return {
        accountNo: key,
        amountIncVAT: null,
        regNo: accountNoValues[0].regNo,
        vehicleID: accountNoValues[0].vehicleID,
        subRows: fetchGroupRows(accountNoValues),
      }
    })
  }

  const exportData = linesWithInvoiceInfo.flatMap((line) => {
    return line.invoiceDetails
  })

  const exportColumns = [
    { key: 'documentNo', header: 'DocumentNr', width: 15 },
    { key: 'accountNo', header: 'KontoNr', width: 15 },
    { key: 'regNo', header: 'RegNr', width: 15 },
    {
      key: 'description',
      header: 'Detaljer',
      width: 50,
    },
    { key: 'lineNo', header: 'LinjeNr', width: 12 },
    {
      key: 'carDescription',
      header: 'Bilbeskrivelse',
      width: 50,
    },
    {
      key: 'amountIncVAT',
      header: 'Beløp',
      width: 20,
    },
  ]

  return (
    <div>
      <div className="flex flex-row justify-between">
        <div>
          <TranslatedText i18nKey={'INVOICEREPORT_DETAILS_HEADING'}>
            {(value) => <Heading>{value}</Heading>}
          </TranslatedText>
          <TranslatedText i18nKey="INVOICE_NO">
            {(value) => (
              <Text variant="lede" level="4" className="">
                {value} {invoiceNo}
              </Text>
            )}
          </TranslatedText>
        </div>
        <ExcelExportButton
          className="h-fit"
          data={exportData}
          label="Eksporter alle"
          config={{
            filename: `InvoiceReport-${invoiceNo}.xlsx`,
            columns: exportColumns,
            sheetName: 'Finance Report',
          }}
        />
      </div>

      <Layout
        grid={null}
        className=""
        variant="default"
        columnGap="medium"
        rowGap="medium"
        paddingTop={'0'}
        paddingBottom={'0'}
      >
        {Object.keys(splitLines).map((invoiceGroupDescription) => {
          const rows = mapNestedRows(collapsableData(splitLines[invoiceGroupDescription]))

          return (
            <div key={invoiceGroupDescription}>
              {invoiceGroupDescription !== 'NO_INVOICE_GROUP' && (
                <Text level="2" className="">
                  {invoiceGroupDescription}
                </Text>
              )}

              <RightAlignedRow>
                <TableExcelExportButton
                  data={rows}
                  config={{
                    filename: `InvoiceReport-${invoiceGroupDescription}.xlsx`,
                    columns: exportColumns,
                    sheetName: 'Finance Report',
                  }}
                />
              </RightAlignedRow>
              <InvoiceReportDetailsTable
                isLoading={isLoading}
                data={collapsableData(splitLines[invoiceGroupDescription])}
                footerData={{
                  amountIncVAT: formatCurrency(sumBy(splitLines[invoiceGroupDescription], 'amountIncVAT')),
                }}
              />
            </div>
          )
        })}

        <Table
          headers={[
            {
              accessor: 'sumTotal',
              i18nKey: 'SUM_TOTAL',
              contentFunction: (row) => formatCurrency(row.value),
            },
          ]}
          data={[{ sumTotal }]}
        />
      </Layout>
    </div>
  )
}

export default InvoiceReportDetails
